import { cardSite, cardTagline, type ResultCard } from "./result-card.js";
import { renderRoundPoster } from "./round-poster-canvas.js";

export const cardWidth = 1080;
export const cardHeight = 1920;

const gold = "#c9a961";
const goldBright = "#e1c47e";
const cream = "#f7f2e6";
const muted = "#cfc9ba";
const serif = '"Playfair Display", Georgia, serif';
const sans = "Inter, system-ui, sans-serif";

/** Draws the card on an offscreen canvas once the fonts are available. */
export async function renderResultCard(
  card: ResultCard,
): Promise<HTMLCanvasElement> {
  await loadCardFonts();
  if (card.poster) return renderRoundPoster(card);
  const canvas = document.createElement("canvas");
  canvas.width = cardWidth;
  canvas.height = cardHeight;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("The image could not be drawn.");
  paintBackground(context, card.accent);
  if (card.artwork) {
    const image = new Image();
    image.src = card.artwork;
    await image.decode();
    const scale = Math.max(
      cardWidth / image.naturalWidth,
      cardHeight / image.naturalHeight,
    );
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    context.drawImage(
      image,
      (cardWidth - width) / 2,
      (cardHeight - height) / 2,
      width,
      height,
    );
    const shade = context.createLinearGradient(0, 0, 0, cardHeight);
    shade.addColorStop(0, "rgba(7,19,16,.65)");
    shade.addColorStop(0.45, "rgba(7,19,16,.86)");
    shade.addColorStop(1, "rgba(7,19,16,.98)");
    context.fillStyle = shade;
    context.fillRect(0, 0, cardWidth, cardHeight);
  }
  paintFrame(context, card.accent);
  paintBody(context, card);
  if (card.memory) paintMemory(context, card.memory);
  return canvas;
}

async function loadCardFonts(): Promise<void> {
  if (typeof document === "undefined" || !("fonts" in document)) return;
  const faces = [
    `700 92px ${serif}`,
    `800 200px ${serif}`,
    `italic 400 46px ${serif}`,
    `600 34px ${sans}`,
    `400 38px ${sans}`,
  ];
  await Promise.all(faces.map((face) => document.fonts.load(face))).catch(
    () => undefined,
  );
}

function paintBackground(
  context: CanvasRenderingContext2D,
  accent: string,
): void {
  const base = context.createLinearGradient(0, 0, 0, cardHeight);
  base.addColorStop(0, "#0b1a14");
  base.addColorStop(0.5, "#071310");
  base.addColorStop(1, "#040b08");
  context.fillStyle = base;
  context.fillRect(0, 0, cardWidth, cardHeight);
  const glow = context.createRadialGradient(540, 720, 40, 540, 720, 760);
  glow.addColorStop(0, withAlpha(accent, 0.2));
  glow.addColorStop(1, withAlpha(accent, 0));
  context.fillStyle = glow;
  context.fillRect(0, 0, cardWidth, cardHeight);
}

function paintFrame(context: CanvasRenderingContext2D, accent: string): void {
  context.strokeStyle = withAlpha(gold, 0.55);
  context.lineWidth = 2;
  for (const y of [232, 1668]) {
    context.beginPath();
    context.moveTo(120, y);
    context.lineTo(cardWidth - 120, y);
    context.stroke();
  }
  context.strokeStyle = withAlpha(accent, 0.35);
  context.lineWidth = 3;
  context.strokeRect(48, 48, cardWidth - 96, cardHeight - 96);
  spacedText(context, "FAIRWAY RIVALRY", 160, `600 34px ${sans}`, gold, 15);
  context.font = `italic 400 46px ${serif}`;
  context.fillStyle = cream;
  context.textAlign = "center";
  context.fillText(cardTagline, cardWidth / 2, 1770);
  spacedText(
    context,
    cardSite.toUpperCase(),
    1846,
    `600 30px ${sans}`,
    gold,
    8,
  );
}

function paintBody(context: CanvasRenderingContext2D, card: ResultCard): void {
  const centre = cardWidth / 2;
  const maxWidth = cardWidth - 200;
  const eyebrow = card.eyebrow.toUpperCase();
  spacedText(context, eyebrow, 440, `600 30px ${sans}`, card.accent, 10);
  context.textAlign = "center";
  context.fillStyle = cream;
  context.font = `700 92px ${serif}`;
  let y = 560;
  for (const line of wrapText(context, card.headline, maxWidth).slice(0, 3)) {
    context.fillText(line, centre, y, maxWidth);
    y += 108;
  }
  y += 80;
  const size = fitSize(context, card.scoreline, maxWidth, 200, 80);
  context.font = `800 ${size}px ${serif}`;
  context.fillStyle = goldBright;
  y += size * 0.75;
  context.fillText(card.scoreline, centre, y, maxWidth);
  y += 70;
  if (card.scoreNote) {
    context.font = `500 40px ${sans}`;
    context.fillStyle = muted;
    const lines = wrapText(context, card.scoreNote, maxWidth).slice(
      0,
      card.artwork ? 4 : 2,
    );
    for (const line of lines) {
      context.fillText(line, centre, y, maxWidth);
      y += 52;
    }
    y += 24;
  }
  context.font = `400 38px ${sans}`;
  context.fillStyle = muted;
  context.fillText(card.detail, centre, Math.max(y + 40, 1440), maxWidth);
}

/** Centred text drawn glyph by glyph so the tracking is exact everywhere. */
function spacedText(
  context: CanvasRenderingContext2D,
  text: string,
  y: number,
  font: string,
  color: string,
  tracking: number,
): void {
  context.font = font;
  context.fillStyle = color;
  context.textAlign = "left";
  const glyphs = [...text];
  const width =
    glyphs.reduce((sum, glyph) => sum + context.measureText(glyph).width, 0) +
    tracking * (glyphs.length - 1);
  let x = (cardWidth - width) / 2;
  for (const glyph of glyphs) {
    context.fillText(glyph, x, y);
    x += context.measureText(glyph).width + tracking;
  }
}

function wrapText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const lines: string[] = [];
  let current = "";
  for (const word of text.split(/\s+/)) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && context.measureText(candidate).width > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/** Largest serif size, stepping down, that keeps `text` inside `maxWidth`. */
function fitSize(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  largest: number,
  smallest: number,
): number {
  for (let size = largest; size > smallest; size -= 10) {
    context.font = `800 ${size}px ${serif}`;
    if (context.measureText(text).width <= maxWidth) return size;
  }
  return smallest;
}

function withAlpha(hex: string, alpha: number): string {
  const value = hex.replace("#", "");
  const full =
    value.length === 3 ? [...value].map((char) => char + char).join("") : value;
  const red = Number.parseInt(full.slice(0, 2), 16);
  const green = Number.parseInt(full.slice(2, 4), 16);
  const blue = Number.parseInt(full.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function paintMemory(
  context: CanvasRenderingContext2D,
  memory: NonNullable<ResultCard["memory"]>,
): void {
  context.textAlign = "center";
  context.fillStyle = muted;
  context.font = `500 23px ${sans}`;
  context.fillText(
    `A MEMORY · RESULT VERSION ${memory.roundVersion}`,
    cardWidth / 2,
    282,
  );
  context.font = `italic 400 30px ${serif}`;
  context.fillStyle = cream;
  const excerpt =
    memory.text.length > 140 ? memory.text.slice(0, 137) + "…" : memory.text;
  const lines = wrapText(context, excerpt, cardWidth - 220);
  lines
    .slice(0, 3)
    .forEach((line, index) =>
      context.fillText(
        line + (index === 2 && lines.length > 3 ? "…" : ""),
        cardWidth / 2,
        326 + index * 36,
        cardWidth - 220,
      ),
    );
}
